import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaLock } from 'react-icons/fa';
import "./css/OvertimePopup.css";
import { BACKEND_URL } from '../constants';
import { useAuth } from '../navigation/AuthProvider';
import axios from 'axios';

const OvertimePopup = ({ onClose, user, company, readOnly }) => {
  const { overtimeRules, setOvertimeRules, setCompany, setLoading } = useAuth();

  const [allOvertimeRules, setAllOvertimeRules] = useState(overtimeRules || []);
  const [editingRule, setEditingRule] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Läs ev. befintliga autosettings (UI för detta är avstängt nedan)
  const initialSettings = company?.autoOvertimeSettings || {};
  const [autoOvertimeEnabled, setAutoOvertimeEnabled] = useState(initialSettings.enabled || false);
  const [autoOvertimeThreshold, setAutoOvertimeThreshold] = useState(initialSettings.monthlyHourThreshold ?? 160);
  const [autoOvertimeRuleId, setAutoOvertimeRuleId] = useState(initialSettings.overtimeRule || "");
  const [autoSettingsChanged, setAutoSettingsChanged] = useState(false);

  // Scrollbar efter 5 poster, visa 5.5 rader (hint). Mäter bara för maxHeight, inte per-rad.
  const firstItemRef = useRef(null);
  const listRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(null);

  useEffect(() => {
    const settings = company?.autoOvertimeSettings || {};
    setAutoOvertimeEnabled(settings.enabled || false);
    setAutoOvertimeThreshold(settings.monthlyHourThreshold ?? 160);
    setAutoOvertimeRuleId(settings.overtimeRule || "");
  }, [company]);

  useEffect(() => {
    if (firstItemRef.current) {
      const h = firstItemRef.current.getBoundingClientRect().height;
      if (h && h > 0) setRowHeight(Math.ceil(h));
    }
  }, [allOvertimeRules, showForm]);

  const handleAdd = () => {
    setEditingRule(null);
    setShowForm(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Är du säker på att du vill ta bort denna övertidsregel? Den kommer automatiskt tas bort från alla skift där den används.")) {
      const { data } = await axios.delete(`${BACKEND_URL}/delete-overtime-rule`, {
        data: {
          companyId: company._id,
          userId: user._id,
          ruleId: id,
        },
      });

      if (data.status !== "success") {
        return alert(data.errorText || "Server fel");
      }

      setAllOvertimeRules(prev => {
        const updated = prev.filter(r => r._id !== id);
        setOvertimeRules(updated);
        return updated;
      });
    }
  };

  const handleSaveRule = async (newRule) => {
    if (editingRule) {
      const { data } = await axios.put(`${BACKEND_URL}/edit-overtime-rule`, {
        companyId: company._id,
        userId: user._id,
        rule: newRule,
      });

      if (data.status !== "success") {
        return alert(data.errorText || "Server fel");
      }

      const updatedRule = data.updatedRule;
      setAllOvertimeRules(prev => {
        const updated = prev.map(r => r._id === updatedRule._id ? updatedRule : r);
        setOvertimeRules(updated);
        return updated;
      });
    } else {
      const { data } = await axios.post(`${BACKEND_URL}/create-new-overtime-rule`, {
        companyId: company._id,
        userId: user._id,
        rule: newRule,
      });

      if (data.status !== "success") {
        return alert(data.errorText || "Server fel");
      }

      const createdRule = data.newRule;
      setAllOvertimeRules(prev => {
        const updated = [...prev, createdRule];
        setOvertimeRules(updated);
        return updated;
      });
    }

    setShowForm(false);
  };

  // Lämnas orörd men knappen för att spara autosettings är disabled (kommer snart).
  const handleSaveAutoSettings = async () => {
    try {
      if (autoOvertimeEnabled) {
        if (!autoOvertimeRuleId) {
          alert("Du måste välja gällande övertidsregel");
          return;
        }
        const threshold = Number(autoOvertimeThreshold);
        if (!Number.isFinite(threshold) || threshold < 0) {
          alert("Du måste ange ett giltigt antal timmar (0 eller mer)");
          return;
        }
      }

      setLoading(true);
      const { data } = await axios.put(`${BACKEND_URL}/update-auto-overtime-settings`, {
        companyId: company._id,
        userId: user._id,
        autoOvertimeEnabled,
        autoOvertimeThreshold,
        autoOvertimeRuleId,
      });

      if (data.status !== "success") {
        alert(data.errorText || "Kunde inte uppdatera automatisk övertid");
        setLoading(false);
        return;
      }

      setCompany(data.company);
      const newSettings = data.company.autoOvertimeSettings || {};
      setAutoOvertimeEnabled(newSettings.enabled || false);
      setAutoOvertimeThreshold(newSettings.monthlyHourThreshold ?? 160);
      setAutoOvertimeRuleId(newSettings.overtimeRule || "");
      setAutoSettingsChanged(false);
      alert("Inställningarna har sparats");
      setLoading(false);
      window.location.reload();
    } catch (err) {
      console.error("❌ Error saving auto overtime settings:", err);
      alert("Ett oväntat fel inträffade. Försök igen.");
    }
  };

  const shouldScroll = allOvertimeRules.length > 5;
  const computedMaxHeight = shouldScroll && rowHeight ? `${rowHeight * 5.5}px` : 'none';

  return (
    <div className="overtime-popup">
      <div className="overtime-popup__overlay" onClick={onClose} />

      <div
        className="overtime-popup__content"
        style={{
          display: 'grid',
       
          gap: 16,
          position: 'relative',
          paddingBottom: 68 /* plats för sticky bottombar */,
        }}
      >
        {/* Vänster: regler (oförändrad look) */}
        <div className="overtime-popup__left">
          <h2 className="overtime-popup__title">Övertidsregler</h2>
          <p className="overtime-popup__description">
            Här kan du skapa olika typer av övertidsersättning (t.ex. 50% eller 100% extra).
            Dessa regler kan sedan användas på specifika skift när en anställd jobbat övertid.
          </p>

          <div
            className="overtime-popup__list"
            role="list"
            ref={listRef}
            style={{
              position: 'relative',
              maxHeight: computedMaxHeight,
              overflowY: shouldScroll ? 'auto' : 'visible',
              paddingRight: 8,
              scrollBehavior: 'smooth',
              /* inget tvång på item-höjder här */
            }}
          >
            {allOvertimeRules.map((rule, index) => (
              <div
                key={rule._id || index}
                className="overtime-popup__item"
                ref={index === 0 ? firstItemRef : null}
                role="listitem"
                /* ingen minHeight här: behåll din ursprungsdesign */
              >
                <div>
                  <strong>{rule.name}</strong> – {rule.percentage}%
                  {rule.description && (
                    <span className="overtime-popup__desc"> ({rule.description})</span>
                  )}
                </div>
                <div className="overtime-popup__actions" style={{ display: 'flex', gap: 12 }}>
                  {!readOnly && (
                    <>
                      <FaEdit
                        onClick={() => handleEdit(rule)}
                        style={{ cursor: 'pointer' }}
                        title="Redigera"
                      />
                      <FaTrash
                        onClick={() => handleDelete(rule._id)}
                        style={{ cursor: 'pointer' }}
                        title="Ta bort"
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Höger: Automatiska övertid — tydligt avskild och avstängd
        <div
          className="overtime-popup__right"
          style={{
            padding: 12,
            border: '1px dashed rgba(0,0,0,0.15)',
            borderRadius: 10,
            background: '#fff',
            position: 'relative',
            opacity: 0.6,
            pointerEvents: 'none',  // ej klickbar
          }}
        >
          <div style={{ position: 'absolute', top: 8, right: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
            <FaLock /> Kommer snart
          </div>

          <h4 className="overtime-popup__auto-title" style={{ marginTop: 0 }}>
            Automatisk övertid (avstängd)
          </h4>
          <p className="overtime-popup__auto-description" style={{ marginTop: 6 }}>
            Denna funktion lanseras senare. Då kan du aktivera automatisk övertid baserat på månadstimmar.
          </p>

          <div className="overtime-popup__checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input type="checkbox" checked={false} readOnly />
            <label>Aktivera automatisk övertid</label>
          </div>

          <div className="overtime-popup__auto-field" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            <label>Max timmar per månad innan övertid</label>
            <input
              type="number"
              value={autoOvertimeThreshold}
              readOnly
              style={{
                padding: '6px 10px',
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.12)',
                background: '#f6f6f6',
              }}
            />
          </div>

          <div className="overtime-popup__auto-field" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            <label>Övertidstyp att använda</label>
            <select
              value={autoOvertimeRuleId}
              readOnly
              style={{
                padding: '6px 10px',
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.12)',
                background: '#f6f6f6',
              }}
            >
              <option>Välj övertidsregel</option>
            </select>
          </div>
        </div> */}

        {/* Sticky bottombar med ENDAST de två knappar du bad om */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            gridColumn: '1 / -1',
            background: 'rgba(255,255,255,0.98)',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            padding: 10,
            display: 'flex',
            gap: 10,
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 2,
          }}
        >
          {!readOnly && (
          <button
            className="overtime-popup__add-btn"
            onClick={handleAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px',
              borderRadius: 10,
              border: 'none',
              background: '#3B82F6',   // samma blå som du ville ha
              color: '#FFFFFF',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <FaPlus /> Lägg till ny övertid
          </button>
          )}

          
          {/* 
          <button
            className="overtime-popup__save-btn"
            onClick={handleSaveAutoSettings}
            disabled={true} // avstängd tills funktionen släpps
            title="Automatisk övertid är avstängd (kommer snart)"
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid rgba(0,0,0,0.08)',
              background: '#f1f1f1',
              color: '#777',
              cursor: 'not-allowed',
              opacity: 0.9,
            }}
          >
            Spara ändringar
          </button> */}
        </div>

        {showForm && (
          <OvertimeFormPopup
            rule={editingRule}
            onClose={() => setShowForm(false)}
            onSave={handleSaveRule}
          />
        )}
      </div>
    </div>
  );
};

const OvertimeFormPopup = ({ rule, onClose, onSave }) => {
  const [name, setName] = useState(rule ? rule.name : "");
  const [percentage, setPercentage] = useState(rule ? rule.percentage : "");
  const [description, setDescription] = useState(rule ? rule.description : "");

  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setPercentage(rule.percentage);
      setDescription(rule.description || "");
    }
  }, [rule]);

  const handleSubmit = () => {
    if (!name || !percentage) return alert("Namn och procent krävs");
    onSave({
      _id: rule ? rule._id : undefined,
      name,
      percentage: parseFloat(percentage),
      description
    });
  };

  return (
    <div className="overtime-form">
      <div className="overtime-form__overlay" onClick={onClose} />

      <div className="overtime-form__content" style={{ position: 'relative' }}>
        <h3>{rule ? "Redigera övertid" : "Ny övertidsregel"}</h3>

        <div className="overtime-form__field" style={{ marginBottom: 10 }}>
          <label>Namn</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="t.ex. Enkel övertid"
          />
        </div>

        <div className="overtime-form__field" style={{ marginBottom: 10 }}>
          <label htmlFor="overtime-percentage">
            Övertidsersättning <span className="highlight">(i % utöver timlönen)</span>
          </label>
          <small className="help-text" style={{ display: 'block', marginBottom: 6, opacity: 0.8 }}>
            T.ex. 50 betyder 50% extra (ger 1,5×). 100% = 2×.
          </small>
          <input
            id="overtime-percentage"
            type="number"
            step="1"
            min="0"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            placeholder="t.ex. 50"
          />
        </div>

        <div className="overtime-form__field" style={{ marginBottom: 16 }}>
          <label>Beskrivning (valfri)</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="t.ex. Gäller efter två timmar"
          />
        </div>

        <div className="overtime-form__actions" style={{ display: 'flex', gap: 10 }}>
          <button className="save" onClick={handleSubmit}>Spara</button>
          <button className="cancel" onClick={onClose}>Avbryt</button>
        </div>
      </div>
    </div>
  );
};

export default OvertimePopup;
